import { Mail, Play, RefreshCw, Square, Trash2, Truck, Image } from 'lucide-react';
import type { ReactNode } from 'react';

import type { StatusTone } from '~/components/ui';
import type { ServiceRecord } from '~/modules/query/services';

/** A container row as the services page reads it. */
export interface ServiceRow extends ServiceRecord {
  _id: string;
  action?: string;
  actionStatus?: string;
}

export interface ServiceAction {
  value: string;
  label: string;
  icon: ReactNode;
  destructive?: boolean;
}

/** The action a row menu item or the bulk bar picked, with the rows it applies to, held while its modal is open. */
export interface PendingAction {
  eventType: string;
  rows: ServiceRow[];
}

export const SERVICE_ACTIONS: ServiceAction[] = [
  { value: 'Start', label: 'Start', icon: <Play size={16} aria-hidden="true" /> },
  { value: 'Stop', label: 'Stop', icon: <Square size={16} aria-hidden="true" /> },
  { value: 'Rebuild', label: 'Rebuild', icon: <RefreshCw size={16} aria-hidden="true" /> },
  { value: 'Move', label: 'Move', icon: <Truck size={16} aria-hidden="true" /> },
  { value: 'Update', label: 'Update', icon: <Image size={16} aria-hidden="true" /> },
  {
    value: 'Remove',
    label: 'Delete',
    icon: <Trash2 size={16} aria-hidden="true" />,
    destructive: true,
  },
];

/** Row menu only: it has no bulk form. */
export const ACKNOWLEDGE_ACTION: ServiceAction = {
  value: 'Acknowledgement',
  label: 'Acknowledge issue',
  icon: <Mail size={16} aria-hidden="true" />,
};

/** Why a service cannot take an action in its current state, or undefined when it can. */
export const serviceActionReason = (
  action: string,
  row: Pick<ServiceRow, 'status'>,
): string | undefined => {
  const status = row.status?.toLowerCase();
  if (action === 'Start' && status === 'running') return 'This service is already running.';
  if (action === 'Stop' && status === 'stopped') return 'This service is already stopped.';
  return undefined;
};

/** API value (lower-cased) -> tone. A value not listed shows as `neutral`. */
export const SERVICE_HEALTH: Record<string, StatusTone> = { up: 'success', down: 'error' };
export const SERVICE_STATUS: Record<string, StatusTone> = {
  running: 'success',
  stopped: 'warning',
};

/** The chip label and tone for a health or status value; empty reads "Unknown". */
export const serviceChip = (
  tones: Record<string, StatusTone>,
  value?: string,
): { label: string; tone: StatusTone } => ({
  label: value || 'Unknown',
  tone: tones[(value ?? '').toLowerCase()] ?? 'neutral',
});

export const SERVICE_STATS_TITLES: Record<string, string> = {
  totalHostCount: 'Total hosts',
  totalServiceCount: 'Total services',
  totalServiceDownCount: 'Services (down)',
  totalServiceUpCount: 'Services (up)',
};
